'use client';

/**
 * Live stats for hangar cards — v4 spec cards carry two KPIs + a foot line.
 * Every number is REAL, pulled from the same APIs the classic UI uses.
 * Cards with no cheap real source simply render without a KPI row (honesty
 * over decoration — no fabricated numbers, F-697).
 */
import { useEffect, useState } from 'react';
import { DECKS, BIZ_SIGIL } from '@/future/shell/decks';
import { BUSINESS_PROJECTS } from '@/lib/business-projects';
import { TABS as CONTENT_STATIONS } from '@/future/surfaces/ContentSuite';

/* single source of truth — the projects deck itself (registry-built) */
const GROUPS = String(DECKS.projects.length);
const GROUP_FOOT = DECKS.projects.map(c => c.sigil || BIZ_SIGIL[c.id] || c.id.slice(0, 2).toUpperCase()).join(' · ');

export type CardStat = {
  k1l: string; k1v: string; k1d?: string; k1up?: boolean;
  k2l?: string; k2v?: string; k2d?: string; k2up?: boolean;
  foot?: string;
  /** real time-series for the card sparkline — no data → no line drawn */
  spark?: number[];
};

const GW = '/api/gw';
const j = (u: string) => fetch(u).then(r => r.json());
const fmt = (n: number) => n >= 1000 ? `${(n / 1000).toFixed(1)}K` : String(n);

/* leads → 8 weekly buckets (oldest→newest) for a REAL sparkline */
const weekly = (dates: number[]): number[] => {
  const now = Date.now(), wk = 7 * 864e5, out = new Array(8).fill(0);
  dates.forEach(t => { const i = Math.floor((now - t) / wk); if (i >= 0 && i < 8) out[7 - i]++; });
  return out;
};

/* lead.site → project slug; sites not owned by a named project belong to the factory (wtf) */
const SITE_OWNER: Record<string, string> = {};
BUSINESS_PROJECTS.forEach(p => { if (p.sites !== 'factory') p.sites.forEach(s => { SITE_OWNER[s.slug] = p.slug; SITE_OWNER[s.domain] = p.slug; }); });
const mainProjects = BUSINESS_PROJECTS.filter(p => !p.slug.startsWith('collab-'));
const collabPeople = BUSINESS_PROJECTS.filter(p => p.slug.startsWith('collab-'));

/* per-deck loaders — each resolves { cardId: stat } */
const LOADERS: Record<string, (emit: (p: Record<string, CardStat>) => void) => Promise<void>> = {
  menu: async (emit) => {
    emit({
      content: { k1l: 'STUDIOS', k1v: String(CONTENT_STATIONS.length), foot: 'VAULT \u2192 QUEUE \u00b7 CLASSIC SUITE' },
      // 9 groups in src/app/settings/page.tsx SETTING_GROUPS (verified 2026-07-08)
      settings: { k1l: 'SYSTEMS', k1v: '9', foot: 'SOMA CALIBRATION' },
      projects: { k1l: 'GROUPS', k1v: GROUPS, foot: GROUP_FOOT },
    });
    await Promise.all([
      j(`${GW}/soma/muscles`).then(d => {
        emit({ beast: { k1l: 'MUSCLES', k1v: String((d.muscles || []).length), k2l: 'HOT', k2v: String((d.muscles || []).filter((m: any) => m.heat >= 70).length), foot: 'BRAIN · NETWORK · PORTFOLIO' } });
      }).catch(() => {}),
      j(`${GW}/sessions`).then(d => {
        const ss = d.sessions || [];
        emit({ chat: { k1l: 'SESSIONS', k1v: String(ss.length), k2l: 'WORKING', k2v: String(ss.filter((s: any) => s.state === 'working').length), foot: 'LIVE GATEWAY STREAMS' } });
        emit({ projects: { k1l: 'GROUPS', k1v: GROUPS, k2l: 'ACTIVE MINDS', k2v: String(ss.filter((s: any) => s.state !== 'hibernating').length), foot: GROUP_FOOT } });
      }).catch(() => {}),
      j('/api/hub/crm/all/leads?limit=500').then(d => {
        const leads = d.leads || [];
        const wk = Date.now() - 7 * 864e5;
        emit({ crm: { k1l: 'LEADS', k1v: fmt(leads.length), k2l: 'NEW 7D', k2v: String(leads.filter((l: any) => new Date(l.created_at).getTime() > wk).length), foot: 'ALL BUSINESSES · HUB SYNC', spark: weekly(leads.map((l: any) => new Date(l.created_at).getTime())) } });
      }).catch(() => {}),
      j('/api/email/stats').then(d => {
        emit({ email: { k1l: 'CONTACTS', k1v: String(d.contacts ?? 0), k2l: 'SENT', k2v: String(d.sent ?? 0), foot: `OPENED ${d.opened ?? 0} · CLICKED ${d.clicked ?? 0}${d.failed ? ` · FAILED ${d.failed}` : ''}` } });
      }).catch(() => {}),
    ]);
  },
  /* projects deck — every card carries REAL numbers from the classic UI's APIs
     (Boss 2026-07-10: 'every single card should represent real information') */
  projects: async (emit) => {
    // instant registry facts (real — single source of truth)
    const base: Record<string, CardStat> = {};
    mainProjects.forEach(p => {
      base[p.slug] = { k1l: 'SITES', k1v: p.sites === 'factory' ? '…' : String(p.sites.length), foot: p.tagline.toUpperCase() };
    });
    base.collabs = { k1l: 'PEOPLE', k1v: String(collabPeople.length), foot: collabPeople.map(c => c.name.toUpperCase()).join(' · ') };
    emit(base);
    // portfolio + CRM both feed the wtf card — await both, emit ONE merged patch (no race)
    const [sitesRes, leadsRes] = await Promise.all([
      j('/api/portfolio').catch(() => []),
      j('/api/hub/crm/all/leads?limit=500').catch(() => ({})),
    ]);
    const sites = Array.isArray(sitesRes) ? sitesRes : [];
    const leads = leadsRes.leads || [];
    const wk7 = Date.now() - 7 * 864e5;
    const byProject: Record<string, any[]> = {};
    leads.forEach((l: any) => { const owner = SITE_OWNER[l.site] || 'wtf'; (byProject[owner] ||= []).push(l); });
    const patch: Record<string, CardStat> = {};
    Object.entries(byProject).forEach(([slug, ls]) => {
      const p = mainProjects.find(m => m.slug === slug); if (!p) return;
      patch[slug] = {
        k1l: 'LEADS', k1v: fmt(ls.length),
        k2l: 'NEW 7D', k2v: String(ls.filter((l: any) => new Date(l.created_at).getTime() > wk7).length),
        foot: p.tagline.toUpperCase(),
        spark: weekly(ls.map((l: any) => new Date(l.created_at).getTime())),
      };
    });
    if (sites.length) {
      const wtfLeads = byProject.wtf || [];
      patch.wtf = {
        k1l: 'SITES', k1v: String(sites.length), k2l: 'LEADS', k2v: fmt(wtfLeads.length),
        foot: `${sites.filter((s: any) => s.status !== 'down').length} LIVE · ${wtfLeads.filter((l: any) => new Date(l.created_at).getTime() > wk7).length} NEW LEADS 7D`,
        spark: weekly(wtfLeads.map((l: any) => new Date(l.created_at).getTime())),
      };
    }
    emit(patch);
    await Promise.all([
      j('/api/ads/accounts').then(d => {
        const rows = d.accounts || d || [];
        const patch: Record<string, CardStat> = {};
        mainProjects.filter(p => p.adsAccountId).forEach(p => {
          const acc = rows.find((a: any) => a.id === p.adsAccountId || a.id === `act_${p.adsAccountId}`);
          if (acc?.lastScore != null) patch[p.slug] = { k1l: 'ADS SCORE', k1v: String(Math.round(acc.lastScore)), k2l: 'FUNNEL', k2v: p.funnelId ? 'LIVE' : '—', foot: p.tagline.toUpperCase() };
        });
        emit(patch);
      }).catch(() => {}),
    ]);
  },
  beast: async (emit) => {
    await Promise.all([
      j(`${GW}/soma/muscles`).then(d => {
        emit({ brain: { k1l: 'MUSCLES', k1v: String((d.muscles || []).length), k2l: 'HOT', k2v: String((d.muscles || []).filter((m: any) => m.heat >= 70).length), foot: 'HEAT-AS-GLOW · DOCTOR' } });
      }).catch(() => {}),
      j(`${GW}/sessions`).then(d => {
        const ss = d.sessions || [];
        emit({ sessions: { k1l: 'MINDS', k1v: String(ss.length), k2l: 'WORKING', k2v: String(ss.filter((s: any) => s.state === 'working').length), foot: 'PARALLEL SOMA SESSIONS' } });
      }).catch(() => {}),
      j('/api/portfolio').then(d => {
        const sites = Array.isArray(d) ? d : [];
        emit({ portfolio: { k1l: 'SITES', k1v: String(sites.length), k2l: 'LIVE', k2v: String(sites.filter((s: any) => s.status !== 'down').length), foot: 'EVERY WEBSITE · ALL NICHES' } });
        emit({ network: { k1l: 'BUSINESSES', k1v: GROUPS, k2l: 'SITES', k2v: String(sites.length), foot: 'EMPIRE WEB' } });
      }).catch(() => {}),
    ]);
  },
  email: async (emit) => {
    await Promise.all([
      j('/api/email/stats').then(d => {
        emit({ inbox: { k1l: 'CONTACTS', k1v: String(d.contacts ?? 0), k2l: 'SENT', k2v: String(d.sent ?? 0), foot: `SCHEDULED ${d.scheduled ?? 0} · FAILED ${d.failed ?? 0}` } });
        emit({ contacts: { k1l: 'CONTACTS', k1v: String(d.contacts ?? 0), foot: 'OUTREACH BOOK' } });
      }).catch(() => {}),
      j('/api/email/projects').then(d => {
        const senders = Array.isArray(d) ? d : [];
        emit({ compose: { k1l: 'SEND AS', k1v: String(senders.length), foot: 'ANY BUSINESS IDENTITY' } });
        emit({ senders: { k1l: 'IDENTITIES', k1v: String(senders.length), foot: 'DOMAINS · DNS · VOLUME' } });
      }).catch(() => {}),
    ]);
  },
};

/* tools:<slug> decks — real numbers on every tool card that has a cheap real source;
   cards without one stay bare (honesty over decoration, F-697) */
const toolsLoader = (slug: string) => async (emit: (p: Record<string, CardStat>) => void) => {
  const p = BUSINESS_PROJECTS.find(b => b.slug === slug);
  if (!p) return;
  const isFactory = p.sites === 'factory';
  if (!isFactory) {
    const n = String((p.sites as any[]).length);
    emit({ websites: { k1l: 'SITES', k1v: n, foot: (p.sites as any[]).map(s => s.domain).join(' · ').toUpperCase() }, performance: { k1l: 'SITES', k1v: n, foot: 'CWV · LIGHTHOUSE · UPTIME' } });
  }
  if (p.funnelId) emit({ funnel: { k1l: 'FUNNEL', k1v: 'LIVE', foot: (p.funnelUrl || '').replace('https://', '').toUpperCase() }, overview: { k1l: 'FUNNEL', k1v: 'LIVE', foot: 'KPI COCKPIT' } });
  await Promise.all([
    isFactory ? j('/api/portfolio').then(d => {
      const sites = Array.isArray(d) ? d : [];
      const patch: Record<string, CardStat> = {
        performance: { k1l: 'SITES', k1v: String(sites.length), k2l: 'LIVE', k2v: String(sites.filter((s: any) => s.status !== 'down').length), foot: 'CWV · LIGHTHOUSE · UPTIME' },
        factory: { k1l: 'SITES', k1v: String(sites.length), foot: 'BUILD · AUDIT · SHIP' },
      };
      emit(patch);
    }).catch(() => {}) : Promise.resolve(),
    p.tabs.includes('crm') ? j('/api/hub/crm/all/leads?limit=500').then(d => {
      const leads = (d.leads || []).filter((l: any) => isFactory ? !SITE_OWNER[l.site] : SITE_OWNER[l.site] === slug);
      const wk7 = Date.now() - 7 * 864e5;
      emit({ crm: { k1l: 'LEADS', k1v: fmt(leads.length), k2l: 'NEW 7D', k2v: String(leads.filter((l: any) => new Date(l.created_at).getTime() > wk7).length), foot: 'HUB SYNC', spark: weekly(leads.map((l: any) => new Date(l.created_at).getTime())) } });
    }).catch(() => {}) : Promise.resolve(),
    p.adsAccountId ? j('/api/ads/accounts').then(d => {
      const acc = (d.accounts || []).find((a: any) => a.id === p.adsAccountId || a.id === `act_${p.adsAccountId}`);
      if (acc?.lastScore != null) emit({ ads: { k1l: 'SCORE', k1v: String(Math.round(acc.lastScore)), k2l: 'AUDIT', k2v: acc.lastAudit ? acc.lastAudit.slice(5, 10) : '—', foot: (acc.name || '').toUpperCase() } });
    }).catch(() => {}) : Promise.resolve(),
    p.tabs.includes('email') ? j('/api/email/stats').then(d => {
      emit({ email: { k1l: 'CONTACTS', k1v: String(d.contacts ?? 0), k2l: 'SENT', k2v: String(d.sent ?? 0), foot: `OPENED ${d.opened ?? 0} · CLICKED ${d.clicked ?? 0}` } });
    }).catch(() => {}) : Promise.resolve(),
  ]);
};

export function useDeckStats(deck: string): Record<string, CardStat> {
  const [stats, setStats] = useState<Record<string, CardStat>>({});
  useEffect(() => {
    const loader = LOADERS[deck] || (deck.startsWith('tools:') && deck !== 'tools:collabs' ? toolsLoader(deck.slice(6)) : undefined);
    if (!loader) return;
    let live = true;
    const pull = () => loader(p => { if (live) setStats(prev => ({ ...prev, ...p })); }).catch(() => {});
    pull();
    const t = setInterval(pull, 60_000); // live-stats law
    return () => { live = false; clearInterval(t); };
  }, [deck]);
  return stats;
}
